<template>
  <section class="list">
    <div class="zone-search">
      <label class="zone-search-label" for="zone-number-search">
        {{
          isVariedadesComercialesSearch
            ? "Buscar bloque de zona (1-33)"
            : "Buscar zona"
        }}
      </label>
      <input
        id="zone-number-search"
        v-model="zoneQuery"
        type="text"
        class="zone-search-input"
        :inputmode="isVariedadesComercialesSearch ? 'numeric' : 'text'"
        :pattern="isVariedadesComercialesSearch ? '[0-9]*' : null"
        :maxlength="isVariedadesComercialesSearch ? 2 : 64"
        :placeholder="
          isVariedadesComercialesSearch ? 'Ej: 24' : 'Ej: R2, DUNA, S-V-25-24'
        "
      />
      <p v-if="zoneSearchError" class="zone-search-error">
        {{ zoneSearchError }}
      </p>
      <p class="zone-search-summary">
        Mostrando {{ filteredZones.length }} de {{ scopedZones.length }} zonas
      </p>
    </div>

    <div class="row" v-if="filteredZones.length > 0">
      <article
        class="card"
        v-for="zone in filteredZones"
        :key="zone.id"
        @click="selectItem(zone)"
      >
        <img
          :src="getImageUrl(zone.photo_url)"
          class="card-image"
          :alt="`Imagen de ${zone.name}`"
        />
        <h3>{{ zone.name }}</h3>
        <p class="zone-code">{{ zone.code }}</p>
      </article>
    </div>

    <p v-else class="zone-empty-message">
      No hay zonas para el filtro seleccionado.
    </p>
  </section>
</template>

<script>
import defaultImage from "@/assets/img/formato/formato.jpeg"; // Usar imagen por defecto

export default {
  name: "ListZones",
  // Recibimos la finca seleccionada y el lomo/variedad opcional para filtrar
  props: {
    finca: {
      type: Object,
      required: true,
    },
    lomo: {
      type: Number,
      default: null,
    },
    variety: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      zones: [],
      zoneQuery: "",
      defaultZoneImage: defaultImage,
    };
  },

  // Observa los cambios en la finca recibida para actualizar las zonas
  watch: {
    finca: {
      immediate: true,
      handler(newVal) {
        this.zones = newVal && Array.isArray(newVal.zones) ? newVal.zones : [];
        this.zoneQuery = "";
      },
    },
  },

  computed: {
    isVariedadesComercialesSearch() {
      const target = "variedades comerciales";
      const farmName = this.normalizeSearchText(this.finca && this.finca.name);
      const farmCode = this.normalizeSearchText(this.finca && this.finca.code);
      return farmName === target || farmCode === target;
    },

    scopedZones() {
      if (this.lomo !== null && this.lomo !== undefined) {
        return this.zones.filter((z) => z.lomo === this.lomo);
      }
      if (this.variety !== null && this.variety !== undefined) {
        return this.zones.filter((z) => z.variety === this.variety);
      }
      return this.zones;
    },

    zoneSearchError() {
      if (!this.isVariedadesComercialesSearch) return "";

      const query = this.zoneQuery.trim();
      if (!query) return "";
      if (!/^\d{1,2}$/.test(query)) return "Introduce un numero entre 1 y 33.";

      const value = Number(query);
      if (value < 1 || value > 33) return "Introduce un numero entre 1 y 33.";

      return "";
    },

    filteredZones() {
      const query = this.zoneQuery.trim();
      if (!query) return this.scopedZones;

      if (!this.isVariedadesComercialesSearch) {
        const normalizedQuery = this.normalizeSearchText(query);
        return this.scopedZones.filter((zone) =>
          this.zoneMatchesText(zone, normalizedQuery),
        );
      }

      if (this.zoneSearchError) return [];

      const numericGroup = Number(query);
      const paddedGroup = String(numericGroup).padStart(2, "0");

      return this.scopedZones.filter((zone) =>
        this.zoneMatchesGroup(zone.code, paddedGroup),
      );
    },
  },

  methods: {
    getImageUrl(photoUrl) {
      if (!photoUrl) {
        return this.defaultZoneImage;
      }

      const normalizedUrl = String(photoUrl).trim();
      if (
        normalizedUrl.startsWith("data:") ||
        normalizedUrl.startsWith("blob:")
      ) {
        return normalizedUrl;
      }

      if (
        normalizedUrl.startsWith("http://") ||
        normalizedUrl.startsWith("https://")
      ) {
        try {
          const parsedUrl = new URL(normalizedUrl);
          if (
            ["localhost", "127.0.0.1", "0.0.0.0"].includes(
              parsedUrl.hostname,
            ) ||
            parsedUrl.hostname.startsWith("10.0.2.")
          ) {
            return `${this.$hostname}${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
          }
          if (parsedUrl.protocol === "http:") {
            parsedUrl.protocol = "https:";
          }
          return parsedUrl.toString();
        } catch {
          return normalizedUrl.replace(/^http:\/\//i, "https://");
        }
      }

      const path = normalizedUrl.startsWith("/")
        ? normalizedUrl
        : `/${normalizedUrl.replace(/^\/+/, "")}`;
      return `${this.$hostname}${path}`;
    },

    normalizeSearchText(value) {
      return String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();
    },

    zoneMatchesText(zone, normalizedQuery) {
      const code = this.normalizeSearchText(zone.code);
      const name = this.normalizeSearchText(zone.name);
      return code.includes(normalizedQuery) || name.includes(normalizedQuery);
    },

    zoneMatchesGroup(code, paddedGroup) {
      const zoneCode = String(code || "").toUpperCase();
      const exactPattern = zoneCode.match(/^S-V-25-(\d{1,2})-R[1-3]$/i);

      if (exactPattern) {
        return String(Number(exactPattern[1])).padStart(2, "0") === paddedGroup;
      }

      const genericPattern = zoneCode.match(/-(\d{1,2})-R\d+$/i);
      if (genericPattern) {
        return (
          String(Number(genericPattern[1])).padStart(2, "0") === paddedGroup
        );
      }

      return false;
    },

    selectItem(zone) {
      this.$root.$emit("zone", zone);
    },
  },
};
</script>

<style scoped>
.zone-search {
  max-width: 1200px;
  margin: 0 auto 12px;
  padding: 0 12px;
}

.zone-search-label {
  display: block;
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #2f2f2f;
}

.zone-search-input {
  width: 100%;
  max-width: 220px;
  border: 1px solid #c8d2c4;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 16px;
  outline: none;
  background: rgba(255, 255, 255, 0.9);
}

.zone-search-input:focus {
  border-color: #107631;
  box-shadow: 0 0 0 2px rgba(16, 118, 49, 0.15);
}

.zone-search-summary {
  margin: 8px 0 0;
  font-size: 12px;
  color: #4f4f4f;
}

.zone-search-error {
  margin: 8px 0 0;
  font-size: 12px;
  color: #b42318;
}

.zone-empty-message {
  max-width: 1200px;
  margin: 10px auto 0;
  padding: 12px;
  text-align: center;
  color: #666;
  font-size: 14px;
}

.zone-code {
  font-size: 0.9em;
  color: #666;
  margin-top: 5px;
}

@media (min-width: 768px) {
  .zone-search {
    padding: 0;
  }
}
</style>
